// components/admin/BannerManager.js - Complete Working Version
import React, { useState, useEffect } from 'react';
import { dbOnValue, dbPush, dbUpdate, dbRemove } from '../../config/firebase';
import { uploadImage, deleteImage, getProperImageUrl } from '../../config/appwrite';
import { Plus, Trash2, Edit2, Upload, X, Image as ImageIcon, AlertCircle, Sun, Palette, Eye, EyeOff, AlignLeft, Type } from 'lucide-react';
import { toast } from 'react-toastify';

const MAX_BANNERS = 3;

const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    buttonText: 'Shop Now',
    buttonLink: '/products/occasions',
    order: 0,
    active: true,
    overlayOpacity: 0.1,
    titleColor: '#ffffff',
    subtitleColor: '#e2e8f0',
    buttonTextColor: '#ffffff',
    buttonBgColor: '#dc2626',
    showTitle: true,
    showSubtitle: true,
    showButton: true,
    textPosition: 'bottom-left'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const unsubscribe = dbOnValue('banners', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const bannerList = Object.entries(data)
          .map(([key, banner]) => ({
            id: key,
            ...banner,
            image: getProperImageUrl(banner.image) || banner.image,
            overlayOpacity: banner.overlayOpacity !== undefined ? banner.overlayOpacity : 0.1,
            titleColor: banner.titleColor || '#ffffff',
            subtitleColor: banner.subtitleColor || '#e2e8f0',
            buttonTextColor: banner.buttonTextColor || '#ffffff',
            buttonBgColor: banner.buttonBgColor || '#dc2626',
            showTitle: banner.showTitle !== false,
            showSubtitle: banner.showSubtitle !== false,
            showButton: banner.showButton !== false,
            textPosition: banner.textPosition || 'bottom-left'
          }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setBanners(bannerList);
      } else {
        setBanners([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!editingBanner && banners.length >= MAX_BANNERS) {
      toast.error(`Maximum ${MAX_BANNERS} banners allowed. Please delete an existing banner first.`);
      return;
    }

    if (!imageFile && !editingBanner?.image) {
      toast.error('Please upload a banner image');
      return;
    }

    setSubmitting(true);
    try {
      let imageData = {};
      if (imageFile) {
        toast.info('Uploading image...');
        const uploadResult = await uploadImage(imageFile);
        imageData = { image: uploadResult.url, imageId: uploadResult.id };
      } else if (editingBanner) {
        imageData = { image: editingBanner.image, imageId: editingBanner.imageId };
      }

      const bannerData = {
        title: formData.title || '',
        subtitle: formData.subtitle || '',
        buttonText: formData.buttonText || 'Shop Now',
        buttonLink: formData.buttonLink || '/products/occasions',
        order: parseInt(formData.order) || 0,
        active: formData.active === true || formData.active === 'true',
        overlayOpacity: parseFloat(formData.overlayOpacity) || 0.1,
        titleColor: formData.titleColor || '#ffffff',
        subtitleColor: formData.subtitleColor || '#e2e8f0',
        buttonTextColor: formData.buttonTextColor || '#ffffff',
        buttonBgColor: formData.buttonBgColor || '#dc2626',
        showTitle: formData.showTitle,
        showSubtitle: formData.showSubtitle,
        showButton: formData.showButton,
        textPosition: formData.textPosition,
        ...imageData,
        updatedAt: new Date().toISOString()
      };

      if (editingBanner) {
        await dbUpdate(`banners/${editingBanner.id}`, bannerData);
        toast.success('Banner updated successfully!');
      } else {
        await dbPush('banners', {
          ...bannerData,
          createdAt: new Date().toISOString()
        });
        toast.success('Banner added successfully!');
      }

      setShowModal(false);
      setEditingBanner(null);
      resetForm();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Failed to save banner: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      buttonText: 'Shop Now',
      buttonLink: '/products/occasions',
      order: banners.length,
      active: true,
      overlayOpacity: 0.1,
      titleColor: '#ffffff',
      subtitleColor: '#e2e8f0',
      buttonTextColor: '#ffffff',
      buttonBgColor: '#dc2626',
      showTitle: true,
      showSubtitle: true,
      showButton: true,
      textPosition: 'bottom-left'
    });
    setImageFile(null);
    setImagePreview('');
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      buttonText: banner.buttonText || 'Shop Now',
      buttonLink: banner.buttonLink || '/products/occasions',
      order: banner.order || 0,
      active: banner.active !== false,
      overlayOpacity: banner.overlayOpacity !== undefined ? banner.overlayOpacity : 0.1,
      titleColor: banner.titleColor || '#ffffff',
      subtitleColor: banner.subtitleColor || '#e2e8f0',
      buttonTextColor: banner.buttonTextColor || '#ffffff',
      buttonBgColor: banner.buttonBgColor || '#dc2626',
      showTitle: banner.showTitle !== false,
      showSubtitle: banner.showSubtitle !== false,
      showButton: banner.showButton !== false,
      textPosition: banner.textPosition || 'bottom-left'
    });
    setImagePreview(banner.image);
    setShowModal(true);
  };

  const handleDelete = async (banner) => {
    try {
      if (banner.imageId) {
        await deleteImage(banner.imageId);
      }
      await dbRemove(`banners/${banner.id}`);
      toast.success('Banner deleted successfully!');
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Failed to delete banner');
    }
  };

  const toggleActive = async (banner) => {
    try {
      await dbUpdate(`banners/${banner.id}`, {
        active: !banner.active,
        updatedAt: new Date().toISOString()
      });
      toast.success(`Banner ${!banner.active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error toggling banner:', error);
      toast.error('Failed to update status');
    }
  };

  const openAddModal = () => {
    if (banners.length >= MAX_BANNERS) {
      toast.error(`Maximum ${MAX_BANNERS} banners allowed. Please delete an existing banner first.`);
      return;
    }
    setEditingBanner(null);
    resetForm();
    setShowModal(true);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a0a0a' }}>Banner Management</h2>
          <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
            Manage homepage advertisement banners (Maximum {MAX_BANNERS} banners)
          </p>
        </div>
        <button
          onClick={openAddModal}
          style={{
            padding: '12px 24px',
            background: banners.length >= MAX_BANNERS ? '#94a3b8' : 'linear-gradient(135deg, #dc2626, #b91c1c)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontWeight: 600,
            cursor: banners.length >= MAX_BANNERS ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: banners.length >= MAX_BANNERS ? 0.6 : 1
          }}
          disabled={banners.length >= MAX_BANNERS}
        >
          <Plus size={18} /> Add Banner ({banners.length}/{MAX_BANNERS})
        </button>
      </div>

      {/* Banner Limit Warning */}
      {banners.length >= MAX_BANNERS && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <AlertCircle size={18} color="#dc2626" />
          <span style={{ fontSize: '0.85rem', color: '#dc2626' }}>
            Maximum {MAX_BANNERS} banners reached. Please delete an existing banner to add a new one.
          </span>
        </div>
      )}

      {/* Banners Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '20px'
      }}>
        {banners.length === 0 ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
            <ImageIcon size={48} color="#cbd5e1" />
            <p style={{ marginTop: '16px', color: '#64748b' }}>No banners added yet. Click "Add Banner" to create one.</p>
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '8px' }}>Maximum {MAX_BANNERS} banners allowed</p>
          </div>
        ) : (
          banners.map(banner => {
            const positionStyle = (() => {
              switch(banner.textPosition) {
                case 'bottom-left': return { bottom: '12px', left: '16px', textAlign: 'left' };
                case 'bottom-right': return { bottom: '12px', right: '16px', textAlign: 'right' };
                case 'top-left': return { top: '12px', left: '16px', textAlign: 'left' };
                case 'top-right': return { top: '12px', right: '16px', textAlign: 'right' };
                case 'center': return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' };
                default: return { bottom: '12px', left: '16px', textAlign: 'left' };
              }
            })();

            return (
              <div key={banner.id} style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                border: `2px solid ${banner.active ? '#dc2626' : '#e2e8f0'}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}>
                {/* Banner Preview */}
                <div style={{
                  position: 'relative',
                  height: '180px',
                  backgroundImage: `linear-gradient(rgba(0,0,0,${banner.overlayOpacity || 0.1}), rgba(0,0,0,${banner.overlayOpacity || 0.1})), url(${banner.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}>
                  <div style={{ position: 'absolute', ...positionStyle }}>
                    {banner.showTitle !== false && banner.title && (
                      <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px', color: banner.titleColor || '#ffffff' }}>{banner.title}</h3>
                    )}
                    {banner.showSubtitle !== false && banner.subtitle && (
                      <p style={{ fontSize: '0.7rem', marginBottom: '8px', color: banner.subtitleColor || '#e2e8f0' }}>{banner.subtitle}</p>
                    )}
                    {banner.showButton !== false && (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: banner.buttonBgColor || '#dc2626',
                        color: banner.buttonTextColor || '#ffffff',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}>
                        {banner.buttonText}
                      </span>
                    )}
                  </div>
                  {!banner.active && (
                    <div style={{ position: 'absolute', top: '8px', right: '8px', background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.6rem', fontWeight: 600 }}>
                      Inactive
                    </div>
                  )}
                </div>
                
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '4px', fontSize: '0.7rem' }}>Order: {banner.order || 0}</span>
                    <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '4px', fontSize: '0.7rem' }}>
                      {banner.showTitle !== false ? '📝 Title' : '🔇 No Title'}
                    </span>
                    <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '4px', fontSize: '0.7rem' }}>
                      {banner.showButton !== false ? '🔘 Button' : '🚫 No Button'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={() => handleEdit(banner)} 
                      style={{ 
                        flex: 1, 
                        padding: '8px', 
                        background: '#e3f2fd', 
                        color: '#1565c0', 
                        border: 'none', 
                        borderRadius: '6px', 
                        fontSize: '0.75rem', 
                        cursor: 'pointer'
                      }}
                    >
                      <Edit2 size={14} /> Edit
                    </button>
                    <button 
                      onClick={() => toggleActive(banner)} 
                      style={{ 
                        flex: 1, 
                        padding: '8px', 
                        background: banner.active ? '#fef2f2' : '#ecfdf5', 
                        color: banner.active ? '#dc2626' : '#10b981', 
                        border: 'none', 
                        borderRadius: '6px', 
                        fontSize: '0.75rem', 
                        cursor: 'pointer'
                      }}
                    >
                      {banner.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      onClick={() => setShowDeleteConfirm(banner)} 
                      style={{ 
                        padding: '8px', 
                        background: '#fef2f2', 
                        color: '#dc2626', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div onClick={() => setShowDeleteConfirm(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '16px', padding: '24px', zIndex: 1051, width: '90%', maxWidth: '400px', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <AlertCircle size={24} color="#dc2626" />
            </div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px' }}>Delete Banner?</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>
              Are you sure you want to delete "{showDeleteConfirm.title || 'this banner'}"? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowDeleteConfirm(null)} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
              <button onClick={() => handleDelete(showDeleteConfirm)} style={{ flex: 1, padding: '10px', background: '#dc2626', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
            </div>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          <div onClick={() => setShowModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '20px', padding: '24px', zIndex: 1001, width: '90%', maxWidth: '750px', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1a0a0a' }}>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' }}>✕</button>
            </div>

            {/* Live Preview Section */}
            {imagePreview && (
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Live Preview</label>
                <div style={{
                  backgroundImage: `linear-gradient(rgba(0,0,0,${formData.overlayOpacity}), rgba(0,0,0,${formData.overlayOpacity})), url(${imagePreview})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: '12px',
                  minHeight: '160px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{ position: 'absolute', ...getPositionStylePreview(formData.textPosition, isMobile) }}>
                    {formData.showTitle && formData.title && (
                      <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '4px', color: formData.titleColor }}>{formData.title}</h3>
                    )}
                    {formData.showSubtitle && formData.subtitle && (
                      <p style={{ fontSize: '11px', marginBottom: '6px', color: formData.subtitleColor }}>{formData.subtitle}</p>
                    )}
                    {formData.showButton && (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: formData.buttonBgColor,
                        color: formData.buttonTextColor,
                        borderRadius: '20px',
                        fontSize: '10px',
                        fontWeight: 600
                      }}>
                        {formData.buttonText} →
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
              {/* Left Column - Image Upload & Basic Info */}
              <div>
                {/* Image Upload */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Banner Image *</label>
                  <div onClick={() => document.getElementById('bannerImage').click()} style={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: '12px',
                    padding: imagePreview ? '16px' : '40px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: imagePreview ? '#f8fafc' : '#fafbfc'
                  }}>
                    <input id="bannerImage" type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                    {imagePreview ? (
                      <div style={{ position: 'relative' }}>
                        <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'contain' }} />
                        <button
                          onClick={(e) => { e.stopPropagation(); setImageFile(null); setImagePreview(''); }}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload size={32} color="#dc2626" style={{ marginBottom: '8px' }} />
                        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Click to upload banner image</p>
                        <p style={{ color: '#94a3b8', fontSize: '0.7rem' }}>Recommended size: 1200 x 400px (Max 5MB)</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Title with Show/Hide Toggle */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={labelStyle}>Title</label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, showTitle: !formData.showTitle })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: formData.showTitle ? '#e3f2fd' : '#f1f5f9',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        color: formData.showTitle ? '#1565c0' : '#64748b'
                      }}
                    >
                      {formData.showTitle ? <Eye size={12} /> : <EyeOff size={12} />}
                      {formData.showTitle ? 'Visible' : 'Hidden'}
                    </button>
                  </div>
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                    placeholder="e.g., Fresh Flowers for Every Occasion" 
                    style={inputModalStyle} 
                  />
                </div>

                {/* Subtitle with Show/Hide Toggle */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={labelStyle}>Subtitle / Description</label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, showSubtitle: !formData.showSubtitle })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: formData.showSubtitle ? '#e3f2fd' : '#f1f5f9',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        color: formData.showSubtitle ? '#1565c0' : '#64748b'
                      }}
                    >
                      {formData.showSubtitle ? <Eye size={12} /> : <EyeOff size={12} />}
                      {formData.showSubtitle ? 'Visible' : 'Hidden'}
                    </button>
                  </div>
                  <textarea 
                    value={formData.subtitle} 
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} 
                    rows={2} 
                    placeholder="Brief description of the offer" 
                    style={{ ...inputModalStyle, resize: 'vertical' }} 
                  />
                </div>

                {/* Button with Show/Hide Toggle */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={labelStyle}>Button</label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, showButton: !formData.showButton })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: formData.showButton ? '#e3f2fd' : '#f1f5f9',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        color: formData.showButton ? '#1565c0' : '#64748b'
                      }}
                    >
                      {formData.showButton ? <Eye size={12} /> : <EyeOff size={12} />}
                      {formData.showButton ? 'Visible' : 'Hidden'}
                    </button>
                  </div>
                  <input 
                    type="text" 
                    value={formData.buttonText} 
                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })} 
                    placeholder="Shop Now" 
                    style={inputModalStyle} 
                  />
                  <input 
                    type="text" 
                    value={formData.buttonLink} 
                    onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })} 
                    placeholder="/products/occasions" 
                    style={{ ...inputModalStyle, marginTop: '8px' }} 
                  />
                </div>
              </div>

              {/* Right Column - Appearance Settings */}
              <div>
                {/* Text Position */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    <AlignLeft size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Text Position
                  </label>
                  <select
                    value={formData.textPosition}
                    onChange={(e) => setFormData({ ...formData, textPosition: e.target.value })}
                    style={inputModalStyle}
                  >
                    <option value="bottom-left">Bottom Left</option>
                    <option value="bottom-right">Bottom Right</option>
                    <option value="top-left">Top Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="center">Center</option>
                  </select>
                </div>

                {/* Overlay Darkness Control - Transparent option */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    <Sun size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Overlay Darkness ({Math.round(formData.overlayOpacity * 100)}%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="0.6"
                    step="0.05"
                    value={formData.overlayOpacity}
                    onChange={(e) => setFormData({ ...formData, overlayOpacity: parseFloat(e.target.value) })}
                    style={{ width: '100%', margin: '8px 0' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#94a3b8' }}>
                    <span>Transparent (0%)</span>
                    <span>Light (30%)</span>
                    <span>Dark (60%)</span>
                  </div>
                  <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '4px' }}>
                    0% = No overlay (image fully visible) | Higher = Better text readability
                  </p>
                </div>

                {/* Title Color */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    <Type size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Title Color
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.titleColor}
                      onChange={(e) => setFormData({ ...formData, titleColor: e.target.value })}
                      style={{ width: '50px', height: '40px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.titleColor}
                      onChange={(e) => setFormData({ ...formData, titleColor: e.target.value })}
                      placeholder="#ffffff"
                      style={{ flex: 1, ...inputModalStyle }}
                    />
                  </div>
                </div>

                {/* Subtitle Color */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    <Type size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Subtitle Color
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.subtitleColor}
                      onChange={(e) => setFormData({ ...formData, subtitleColor: e.target.value })}
                      style={{ width: '50px', height: '40px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.subtitleColor}
                      onChange={(e) => setFormData({ ...formData, subtitleColor: e.target.value })}
                      placeholder="#e2e8f0"
                      style={{ flex: 1, ...inputModalStyle }}
                    />
                  </div>
                </div>

                {/* Button Text Color */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    <Type size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Button Text Color
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.buttonTextColor}
                      onChange={(e) => setFormData({ ...formData, buttonTextColor: e.target.value })}
                      style={{ width: '50px', height: '40px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.buttonTextColor}
                      onChange={(e) => setFormData({ ...formData, buttonTextColor: e.target.value })}
                      placeholder="#ffffff"
                      style={{ flex: 1, ...inputModalStyle }}
                    />
                  </div>
                </div>

                {/* Button Background Color */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Button Background Color</label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.buttonBgColor}
                      onChange={(e) => setFormData({ ...formData, buttonBgColor: e.target.value })}
                      style={{ width: '50px', height: '40px', borderRadius: '8px', border: '1px solid #e2e8f0', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.buttonBgColor}
                      onChange={(e) => setFormData({ ...formData, buttonBgColor: e.target.value })}
                      placeholder="#dc2626"
                      style={{ flex: 1, ...inputModalStyle }}
                    />
                  </div>
                </div>

                {/* Order & Active */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  <div>
                    <label style={labelStyle}>Display Order</label>
                    <input 
                      type="number" 
                      value={formData.order} 
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} 
                      style={inputModalStyle} 
                      min="0"
                    />
                    <p style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '4px' }}>Lower number = Higher priority</p>
                  </div>
                  <div>
                    <label style={labelStyle}>Status</label>
                    <select 
                      value={formData.active} 
                      onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })} 
                      style={inputModalStyle}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button 
                onClick={() => setShowModal(false)} 
                style={{ flex: 1, padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '10px', fontWeight: 500, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmit} 
                disabled={submitting} 
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: submitting ? '#94a3b8' : '#dc2626', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '10px', 
                  fontWeight: 600, 
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
              >
                {submitting ? 'Saving...' : (editingBanner ? 'Update Banner' : 'Add Banner')}
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// Helper function for preview position
const getPositionStylePreview = (position, isMobile) => {
  const baseStyle = { position: 'absolute', zIndex: 2, maxWidth: '85%' };
  switch(position) {
    case 'bottom-left': return { ...baseStyle, bottom: '12px', left: '16px', textAlign: 'left' };
    case 'bottom-right': return { ...baseStyle, bottom: '12px', right: '16px', textAlign: 'right' };
    case 'top-left': return { ...baseStyle, top: '12px', left: '16px', textAlign: 'left' };
    case 'top-right': return { ...baseStyle, top: '12px', right: '16px', textAlign: 'right' };
    case 'center': return { ...baseStyle, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' };
    default: return { ...baseStyle, bottom: '12px', left: '16px', textAlign: 'left' };
  }
};

const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '6px' };
const inputModalStyle = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', outline: 'none' };

export default BannerManager;